import React from 'react';
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WBBox, WBButton, WBFlex, WBTypography } from '@admiin-com/ds-web';
import PdfThumbnail from '../../../../../components/PdfThumbnail/PdfThumbnail';
import AutoCompleteLookupV2 from '../../../../../components/AutoCompleteLookup/AutoCompleteLookupV2';
import { useTaskCreationContext } from '../../../../providers/TaskCreationProvider/useTaskCreationContext';
import { S3Upload } from '@admiin-com/ds-common';
import { S3UploadType } from '@admiin-com/ds-graphql';
import { List, ListItem } from '@mui/material';
import { BillCreateFormData } from '../../../../providers/TaskCreationProvider/TaskCreationProvider';

interface DocumentMapping {
  document: S3Upload;
  to: any;
}

interface TaxBindingToAssignProps {
  onNext: () => void;
  onAssignEntity?: (
    documentKey: string | null | undefined,
    entity: any
  ) => void;
}

export const TaxBindingToAssign = ({
  onNext,
  onAssignEntity,
}: TaxBindingToAssignProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const taxes = useWatch({ control, name: 'taxes' });
  const { update } = useFieldArray({
    control: control,
    name: 'taxes',
  });
  const { client } = useTaskCreationContext();

  // Get the documents from the form data

  // Create document mappings state
  const [documentMappings, setDocumentMappings] = React.useState<
    DocumentMapping[]
  >([]);

  React.useEffect(() => {
    const mappings = taxes.map((tax: any) => ({
      document: tax.documents?.[0],
      to: tax.to || null,
    }));
    setDocumentMappings(mappings);

    mappings.forEach((mapping: DocumentMapping) => {
      if (mapping.document?.key) {
        onAssignEntity?.(mapping.document.key, mapping.to);
      }
    });
  }, [onAssignEntity, taxes]);

  console.log(taxes);

  // Handle updating the "to" selection for a specific document
  const handleToChange = (index: number, toValue: any) => {
    const updatedMappings = [...documentMappings];
    updatedMappings[index].to = toValue;
    setDocumentMappings(updatedMappings);

    const documentKey = updatedMappings[index].document?.key;
    if (documentKey) {
      onAssignEntity?.(documentKey, toValue);
    }

    update(index, {
      ...taxes[index],
      to: toValue,
      futureTaskInput: {
        ...taxes[index].futureTaskInput,
        to: toValue,
      },
    });
  };

  // Check if all documents have been assigned
  const canProceed = documentMappings.every((mapping) => mapping.to !== null);

  // Get filename from document
  const getDocumentName = (document: S3Upload | null) => {
    return document?.name || document?.key?.split('/').pop() || 'Document';
  };

  return (
    <WBBox p={3}>
      <WBTypography variant="h4" mb={3}>
        {t('assignDocumentsToRecipients', {
          ns: 'taskbox',
          defaultValue: 'Assign Documents to Recipients',
        })}
      </WBTypography>

      <WBTypography variant="body2" color="text.secondary" mb={4}>
        {t('assignDocumentsDescription', {
          ns: 'taskbox',
          defaultValue:
            'Please assign each document to the appropriate recipient. You can see a thumbnail and filename for each document.',
        })}
      </WBTypography>

      <List>
        {documentMappings.map((mapping, index) => (
          <ListItem
            key={index}
            sx={{
              p: 2,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <WBFlex
              alignItems="center"
              gap={4}
              width="100%"
              flexDirection={['column', 'row']}
            >
              {/* Document Thumbnail */}

              <WBFlex
                gap={2}
                justifyContent={['flex-start', 'center']}
                width={['100%', 'auto']}
              >
                <WBBox
                  sx={{
                    width: '100px',
                    height: '100px',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {mapping.document && mapping.document.src && (
                    <PdfThumbnail
                      documentUrl={mapping.document.src}
                      isImage={mapping.document.type === S3UploadType.IMAGE}
                    />
                  )}
                </WBBox>

                {/* Document Info */}
                <WBBox>
                  <WBTypography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      wordBreak: 'break-word',
                      mb: 0.5,
                    }}
                  >
                    {getDocumentName(mapping.document)}
                  </WBTypography>
                  {mapping.document && (
                    <WBTypography variant="caption" color="text.secondary">
                      {mapping.document.type === S3UploadType.IMAGE
                        ? 'Image'
                        : 'PDF'}
                    </WBTypography>
                  )}
                </WBBox>
              </WBFlex>

              {/* Assignment Selector */}
              <WBFlex
                sx={{ flex: 1, justifyContent: 'flex-end' }}
                width={['100%', 'auto']}
              >
                <WBFlex sx={{ flex: 1, minWidth: 0, maxWidth: '300px' }}>
                  <AutoCompleteLookupV2
                    value={mapping.to}
                    onChange={(value) => handleToChange(index, value)}
                    label={t('assignTo', {
                      ns: 'taskbox',
                      defaultValue: 'Assign To',
                    })}
                    placeholder={t('entitySearchPlaceholder', {
                      ns: 'taskbox',
                    })}
                    type={'Entity'}
                    noPopupIcon
                    clientGroupCode={client?.clientGroupCode}
                    client={client}
                    error={!mapping.to}
                    helperText={
                      !mapping.to
                        ? t('pleaseSelectRecipient', {
                            ns: 'taskbox',
                            defaultValue: 'Please select a recipient',
                          })
                        : ''
                    }
                  />
                </WBFlex>
              </WBFlex>
            </WBFlex>
          </ListItem>
        ))}
      </List>

      {/* Summary */}
      <WBBox
        my={4}
        p={2}
        bgcolor="background.paper"
        borderRadius={1}
        border="1px solid"
        borderColor="divider"
      >
        <WBFlex alignItems="center" justifyContent="space-between">
          <WBTypography variant="body2">
            {t('assignmentProgress', {
              ns: 'taskbox',
              defaultValue: 'Assignment Progress',
            })}
          </WBTypography>
          <WBTypography variant="body2" fontWeight={600}>
            {documentMappings.filter((m) => m.to).length} /{' '}
            {documentMappings.length}{' '}
            {t('completed', { ns: 'taskbox', defaultValue: 'completed' })}
          </WBTypography>
        </WBFlex>
      </WBBox>

      {/* Next Button */}
      <WBBox display="flex" justifyContent={['center', 'flex-end']}>
        <WBButton onClick={onNext} disabled={!canProceed} variant="contained">
          {t('continueToFill', {
            ns: 'taskbox',
            defaultValue: 'Continue to Fill Documents',
          })}
        </WBButton>
      </WBBox>
    </WBBox>
  );
};
